import React from 'react';
import { Title, Loader, ReloadIconButton } from '../../../elements';
import { LoaderSize } from '../../../constants/constants';
import { extractApiErrorMessage, notifications } from '../../../utils';
import { MedicalSupplyUseExistingModal } from '../../../components';
import { MedicalSupplyType, medicalSupplyMethodKeys } from '../../../models/injection';
import {
  Grid,
  Icon,
  Chip,
  Paper,
  TableRow,
  TableBody,
  TableCell,
  Table,
  TableContainer,
  Typography,
  Box,
  IconButton,
  Tooltip
} from '@material-ui/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHospital } from '@fortawesome/free-solid-svg-icons';
import { useStyles } from './styles';
import { useTranslation } from 'react-i18next';
import { HealthSystemDomainMedicalSuppliesResultFarmMedicalSupplyViewModel } from '../../../api/HealthSystemApi';
import { ApiService } from '../../../hooks';
import { AnimalType } from '../../../models/animal';

const MedicalSuppliesPage: React.FC<any> = () => {
  const classes = useStyles();
  const apiService = new ApiService();
  const { t } = useTranslation();

  const [total, setTotal] = React.useState(0);
  const [supplies, setSupplies] = React.useState<HealthSystemDomainMedicalSuppliesResultFarmMedicalSupplyViewModel[]>(
    []
  );
  const [isBusy, setIsBusy] = React.useState(false);
  const [loaded, setLoaded] = React.useState(false);

  const [useSupplyOpened, setUseSupplyOpened] = React.useState(false);

  const loadFarmMedicalSupplies = async () => {
    setIsBusy(true);

    apiService.api.medicalSupplies
      .medicalSuppliesGetFarmMedicalSupplies({})
      .then((result) => {
        setSupplies(result.data);
        setTotal(result.data.length);
      })
      .catch((e) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      })
      .finally(() => {
        setIsBusy(false);
        setLoaded(true);
      });
  };

  React.useEffect(() => {
    if (!loaded) {
      const loadData = async () => loadFarmMedicalSupplies();
      loadData();
    }
  }, [loaded]);

  const getVaccinations = (): HealthSystemDomainMedicalSuppliesResultFarmMedicalSupplyViewModel[] => {
    return supplies.filter((x) => x.supplyType === MedicalSupplyType.Vaccination);
  };

  const getAntibiotics = (): HealthSystemDomainMedicalSuppliesResultFarmMedicalSupplyViewModel[] => {
    return supplies.filter((x) => x.supplyType === MedicalSupplyType.Antibiotic);
  };

  const handleAddFarmSupplyClose = (result: boolean) => {
    setUseSupplyOpened(false);

    if (result) {
      setLoaded(false);
    }
  };

  const renderWaitingTimeCell = (supply: HealthSystemDomainMedicalSuppliesResultFarmMedicalSupplyViewModel) => {
    return supply.waitingTime === 0 ? '-' : t('f-com-number-days', { number: supply.waitingTime });
  };

  const renderAnimalTypeCell = (supply: HealthSystemDomainMedicalSuppliesResultFarmMedicalSupplyViewModel) => {
    return supply.animalType === 0 ? '-' : AnimalType[supply.animalType];
  };

  const renderMethodCell = (supply: HealthSystemDomainMedicalSuppliesResultFarmMedicalSupplyViewModel) => {
    const key = medicalSupplyMethodKeys[supply.method];
    return key ? t(key) : '-';
  };

  const renderMedicalSupplyRow = (supply: HealthSystemDomainMedicalSuppliesResultFarmMedicalSupplyViewModel) => {
    return (
      <TableRow key={supply.id} hover>
        <TableCell scope="row" align="right" variant="head">
          {supply.number}
        </TableCell>
        <TableCell align="center">{supply.producer || '-'}</TableCell>
        <TableCell align="center">{renderAnimalTypeCell(supply)}</TableCell>
        <TableCell align="center">{supply.inStock.toFixed(2)}</TableCell>
        <TableCell align="center">{renderWaitingTimeCell(supply)}</TableCell>
        <TableCell align="center">{renderMethodCell(supply)}</TableCell>
      </TableRow>
    );
  };

  const renderTableHeader = (supplyType: string) => {
    return (
      <TableRow>
        <TableCell align="right" variant="head">
          <Typography variant={'h6'}>{supplyType}</Typography>
        </TableCell>
        <TableCell align="center" variant="head">
          <Typography variant={'h6'}>{t('f-supplies-producer')}</Typography>
        </TableCell>
        <TableCell align="center" variant="head">
          <Typography variant={'h6'}>{t('f-com-animal-type')}</Typography>
        </TableCell>
        <TableCell align="center" variant="head">
          <Typography variant={'h6'}>{t('f-supp-in-stock')}</Typography>
        </TableCell>
        <TableCell align="center" variant="head">
          <Typography variant={'h6'}>{t('f-supp-withdrawal-per')}</Typography>
        </TableCell>
        <TableCell align="center" variant="head">
          <Typography variant={'h6'}>{t('f-supplies-method')}</Typography>
        </TableCell>
      </TableRow>
    );
  };

  const renderEmptyTableRow = (labelText: string) => {
    return (
      <TableRow>
        <TableCell variant="footer">
          <Chip className={classes.tableInfoChip} label={labelText} />
        </TableCell>
        <TableCell></TableCell>
        <TableCell></TableCell>
        <TableCell></TableCell>
        <TableCell></TableCell>
        <TableCell></TableCell>
      </TableRow>
    );
  };

  return (
    <>
      <Grid container spacing={3}>
        <Grid item xs={12} md={12}>
          <Grid container direction="row" justifyContent="space-between" alignItems="center">
            <Grid item>
              <Grid item container spacing={1}>
                <FontAwesomeIcon icon={faHospital} size="2x" className={classes.headerIcon} />
                <Box ml={1}>
                  <Title>{t('f-supp-farm')}</Title>
                </Box>
                <Grid item>
                  <Chip label={total} variant="outlined" />
                </Grid>
              </Grid>
            </Grid>

            <Grid item>
              <ReloadIconButton action={() => setLoaded(false)} />
              <Tooltip title={t('f-supp-add-exist-title')} arrow>
                <IconButton id="useSupplyBtn" color="primary" onClick={() => setUseSupplyOpened(true)}>
                  <Icon>add_circle</Icon>
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>

          <Loader isLoading={isBusy} size={LoaderSize.DEFAULT_SIZE} />

          {/* data table */}
          {loaded && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TableContainer component={Paper} elevation={1} className={classes.mt2}>
                  <Table size="small">
                    <TableBody>
                      {/* Vaccinations */}
                      {renderTableHeader(t('f-supp-vaccination'))}
                      {getVaccinations().map((supply) => renderMedicalSupplyRow(supply))}
                      {getVaccinations().length === 0 && renderEmptyTableRow('No vaccination currently in use')}

                      {/* Medications */}
                      {renderTableHeader(t('f-supp-antibiotic'))}
                      {getAntibiotics().map((supply) => renderMedicalSupplyRow(supply))}
                      {getAntibiotics().length === 0 && renderEmptyTableRow('No medication currently in use')}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            </Grid>
          )}
        </Grid>
      </Grid>

      <MedicalSupplyUseExistingModal opened={useSupplyOpened} onModalAction={handleAddFarmSupplyClose} />
    </>
  );
};

export default MedicalSuppliesPage;
